import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuth } from './context/AuthContext';
import Layout from './components/layout/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import ComingSoon from './pages/ComingSoon';
import IntakeBatches from './pages/IntakeBatches';
import IntakeUpload from './pages/IntakeUpload';
import IntakeBatchDetail from './pages/IntakeBatchDetail';
import OrderDetail from './pages/OrderDetail';
import DispatchReady from './pages/DispatchReady';
import DispatchBatches from './pages/DispatchBatches';
import DispatchBatchDetail from './pages/DispatchBatchDetail';
import TrackingUpload from './pages/TrackingUpload';
import ShareWithOps from './pages/ShareWithOps';
import MboExport from './pages/MboExport';
import ScheduledSplits from './pages/ScheduledSplits';
import PendingTracking from './pages/PendingTracking';
import CarrierTracking from './pages/CarrierTracking';
import OpsQueue from './pages/OpsQueue';
import OpsAllOrders from './pages/OpsAllOrders';
import DataQuality from './pages/DataQuality';
import ActivityLog from './pages/ActivityLog';
import Users from './pages/Users';
import Vendors from './pages/Vendors';
import StatusReport from './pages/StatusReport';
import SalesReport from './pages/SalesReport';
import VendorPayables from './pages/VendorPayables';
import OwnerCockpit from './pages/OwnerCockpit';
import OwnerAnalytics from './pages/OwnerAnalytics';
import AiChat from './pages/AiChat';
import OrderByExternal from './pages/OrderByExternal';
import PaymentReview from './pages/PaymentReview';
import MoneyAtRisk from './pages/MoneyAtRisk';
import VendorFollowup from './pages/VendorFollowup';
import TrackingHealth from './pages/TrackingHealth';
import DispatchDesk from './pages/DispatchDesk';
import OpsDesk from './pages/OpsDesk';
import AdminProductMapping from './pages/AdminProductMapping';
import AdminCleanup from './pages/AdminCleanup';
import AdminVendorMapping from './pages/AdminVendorMapping';
import AdminShipperValidation from './pages/AdminShipperValidation';
import ValidationDashboard from './pages/ValidationDashboard';
import MyQueue from './pages/MyQueue';
import HoldOrders from './pages/HoldOrders';
import Settings from './pages/Settings';

function Protected() {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-6 text-sm text-gray-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Outlet />;
}

function RequireRole({ roles, children }) {
  const { user } = useAuth();
  const ok = user?.roles?.some((r) => roles.includes(r) || r === 'super_admin');
  if (!ok) return <ComingSoon title="Forbidden" sprint={null} />;
  return children;
}

export default function App() {
  return (
    <>
      <Toaster position="top-right" />
      <Routes>
        <Route path="/login" element={<Login />} />

        <Route element={<Protected />}>
          <Route element={<Layout />}>
            <Route path="/" element={<Dashboard />} />

            {/* Intake — Phase 2 */}
            <Route path="/intake" element={<IntakeBatches />} />
            <Route path="/intake/upload" element={<IntakeUpload />} />
            <Route path="/intake/:id" element={<IntakeBatchDetail />} />

            {/* Orders — Phase 3 */}
            <Route path="/orders/:id" element={<OrderDetail />} />

            {/* Dispatch — Phase 4 */}
            <Route path="/dispatch/ready" element={<DispatchReady />} />
            <Route path="/dispatch" element={<DispatchBatches />} />
            <Route path="/dispatch/:id" element={<DispatchBatchDetail />} />

            {/* Scheduled splits + Pending tracking — v1 parity */}
            <Route path="/scheduled-splits" element={<ScheduledSplits />} />
            <Route path="/pending-tracking" element={<PendingTracking />} />

            {/* Tracking — Phase 5 */}
            <Route path="/tracking/upload" element={<TrackingUpload />} />
            <Route path="/tracking/share-with-ops" element={<ShareWithOps />} />
            <Route path="/tracking/mbo-export" element={<MboExport />} />
            <Route path="/tracking/carrier" element={<CarrierTracking />} />

            {/* Global order search — accessible to admin + ops */}
            <Route path="/search" element={<OpsAllOrders />} />

            {/* Ops — Phase 6 */}
            <Route path="/ops" element={<OpsQueue />} />
            <Route path="/ops/all" element={<OpsAllOrders />} />

            {/* Admin — Phase 7 */}
            <Route path="/admin/data-quality" element={<DataQuality />} />

            {/* Admin — Phase 8 */}
            <Route path="/admin/users" element={
              <RequireRole roles={['super_admin']}><Users /></RequireRole>
            } />
            <Route path="/admin/vendors" element={
              <RequireRole roles={['dispatch_admin']}><Vendors /></RequireRole>
            } />
            <Route path="/admin/status-report" element={
              <RequireRole roles={['dispatch_admin']}><StatusReport /></RequireRole>
            } />
            <Route path="/admin/sales-report" element={
              <RequireRole roles={['dispatch_admin', 'super_admin']}><SalesReport /></RequireRole>
            } />
            <Route path="/admin/vendor-followup" element={
              <RequireRole roles={['dispatch_admin', 'dispatch_user', 'super_admin']}><VendorFollowup /></RequireRole>
            } />
            <Route path="/admin/tracking-health" element={
              <RequireRole roles={['dispatch_admin', 'dispatch_user', 'ops_team', 'super_admin']}><TrackingHealth /></RequireRole>
            } />
            <Route path="/ops/desk" element={
              <RequireRole roles={['ops_team', 'dispatch_admin', 'super_admin']}><OpsDesk /></RequireRole>
            } />
            <Route path="/admin/dispatch-desk" element={
              <RequireRole roles={['dispatch_admin', 'super_admin']}><DispatchDesk /></RequireRole>
            } />
            <Route path="/admin/cockpit" element={
              <RequireRole roles={['super_admin']}><OwnerCockpit /></RequireRole>
            } />
            <Route path="/admin/analytics" element={
              <RequireRole roles={['super_admin']}><OwnerAnalytics /></RequireRole>
            } />
            <Route path="/admin/vendor-payables" element={
              <RequireRole roles={['super_admin']}><VendorPayables /></RequireRole>
            } />
            <Route path="/admin/ai-chat" element={
              <RequireRole roles={['super_admin']}><AiChat /></RequireRole>
            } />
            <Route path="/admin/payment-review" element={
              <RequireRole roles={['super_admin']}><PaymentReview /></RequireRole>
            } />
            <Route path="/admin/money-at-risk" element={
              <RequireRole roles={['super_admin']}><MoneyAtRisk /></RequireRole>
            } />
            <Route path="/orders/by-external/:ext" element={<OrderByExternal />} />
            <Route path="/admin/product-mapping" element={
              <RequireRole roles={['dispatch_admin']}><AdminProductMapping /></RequireRole>
            } />
            <Route path="/admin/cleanup" element={
              <RequireRole roles={['dispatch_admin', 'super_admin']}><AdminCleanup /></RequireRole>
            } />
            <Route path="/admin/vendor-mapping" element={
              <RequireRole roles={['dispatch_admin']}><AdminVendorMapping /></RequireRole>
            } />
            <Route path="/admin/shipper-rules" element={
              <RequireRole roles={['dispatch_admin']}><AdminShipperValidation /></RequireRole>
            } />
            <Route path="/admin/validation-dashboard" element={
              <RequireRole roles={['dispatch_admin']}><ValidationDashboard /></RequireRole>
            } />
            <Route path="/hold-orders" element={<HoldOrders />} />

            {/* Super admin — Phase 7 */}
            <Route path="/admin/activity-logs" element={
              <RequireRole roles={['super_admin']}>
                <ActivityLog />
              </RequireRole>
            } />

            {/* My Queue — D4 (UAT round 3): admin + user landing for orders
                awaiting their action. */}
            <Route path="/my-queue" element={<MyQueue />} />

            {/* Settings — self-service password change */}
            <Route path="/settings" element={<Settings />} />

            <Route path="*" element={<ComingSoon title="Not found" sprint={null} />} />
          </Route>
        </Route>
      </Routes>
    </>
  );
}
